import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Key, Copy, Check, RefreshCw, Trash2, AlertTriangle, ExternalLink, Loader, Inbox,
} from "lucide-react";
import { AuthContext } from "../App";
import { useTheme } from "../contexts/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Sección de gestión del token de la API de Stock para un cliente concreto.
 *
 * FLUJO DE SEGURIDAD:
 *  - El que genera/regenera NO recibe el token en la respuesta. El token plano
 *    queda en espera (`pending_claim`) y solo lo puede ver UNA VEZ el cliente
 *    matriz desde su panel de API Keys. Si caduca (24h) hay que volver a
 *    solicitar a CentralSalud.
 *  - Esto significa: CentralSalud NUNCA conoce los tokens emitidos.
 *
 * Roles que llegan a este componente:
 *  - Admin (desde edición de cliente): solo solicita; NO ve token; ve estado.
 *  - Cliente matriz (desde "Mi perfil" → API Keys): puede regenerar/revocar
 *    Y reclamar el token pendiente. Cuando reclama, ve el token UNA VEZ.
 */
export default function StockApiTokenSection({ clienteId, modoStock = "inactivo", docsHref = "/api-docs/stock" }) {
  const { user } = useContext(AuthContext);
  const { theme } = useTheme();
  const esAdmin = user?.permisos === 1;
  const stockInactivo = modoStock === "inactivo";

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [tokenJustClaimed, setTokenJustClaimed] = useState(null);
  const [copied, setCopied] = useState(false);

  const cargar = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/clientes/${clienteId}/stock-api-token/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Error al cargar info del token");
      setInfo(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  // ─── Solicitar / regenerar token ───────────────────────────────────────
  const handleSolicitar = async () => {
    const accion = info?.activo ? "regenerar" : "solicitar";
    const sufijo = info?.activo
      ? "\n\n⚠️ Hay un token activo. Al regenerar, el anterior queda invalidado INMEDIATAMENTE."
      : "";
    const aviso = esAdmin
      ? `Vas a ${accion} un token para este cliente. Recuerda: CentralSalud NO verá el token. El cliente lo reclamará desde su panel "API Keys".${sufijo}`
      : `Vas a ${accion} el token. Lo verás UNA SOLA VEZ tras la generación.${sufijo}`;
    if (!window.confirm(aviso)) return;

    setWorking(true); setError("");
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/clientes/${clienteId}/stock-api-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Error al generar el token");
      await cargar();
      // Si soy el cliente matriz (no admin), reclamo el token automáticamente
      // para ver el plano UNA VEZ sin necesidad de un segundo clic.
      if (!esAdmin) {
        await reclamarToken();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  // ─── Reclamar token pendiente (solo cliente matriz) ────────────────────
  const reclamarToken = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/clientes/${clienteId}/stock-api-token/claim`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Error al reclamar el token");
      setTokenJustClaimed(data.token);
      await cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  // ─── Revocar ───────────────────────────────────────────────────────────
  const handleRevocar = async () => {
    if (!window.confirm(
      "⚠️ Vas a REVOCAR el token actual.\n\n" +
      "Todas las integraciones que lo estén usando empezarán a fallar inmediatamente. " +
      "Esta acción no se puede deshacer (pero se puede generar uno nuevo después).\n\n¿Continuar?"
    )) return;
    setWorking(true); setError("");
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/clientes/${clienteId}/stock-api-token`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Error al revocar token");
      setTokenJustClaimed(null);
      cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  const handleCopiar = async () => {
    if (!tokenJustClaimed) return;
    try {
      await navigator.clipboard.writeText(tokenJustClaimed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      window.prompt("Copia este token:", tokenJustClaimed);
    }
  };

  if (loading && !info) {
    return (
      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2 text-sm text-gray-500">
        <Loader size={14} className="animate-spin" /> Cargando información del token...
      </div>
    );
  }

  const puedeReclamar = !esAdmin && info?.pending_claim;

  return (
    <div className="mt-5 p-4 rounded-xl border-2" style={{ borderColor: theme.primaryBorder, backgroundColor: theme.primaryLight }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Key className="mt-0.5" size={18} style={{ color: theme.primary }} />
          <div>
            <h4 className="text-sm font-bold text-gray-800">API de Stock — token de integración</h4>
            <p className="text-xs text-gray-600 mt-0.5">
              {esAdmin
                ? "Solo administradores de CentralSalud pueden iniciar la generación. CentralSalud nunca verá el token: lo reclama el cliente matriz desde su panel."
                : "Permite a tus sistemas externos registrar entradas/salidas y consultar inventario por HTTP."}
            </p>
          </div>
        </div>
        <a
          href={docsHref}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs font-semibold whitespace-nowrap px-2.5 py-1.5 rounded border hover:bg-white transition-colors"
          style={{ color: theme.primary, borderColor: theme.primaryBorder }}
        >
          <ExternalLink size={12} /> Documentación
        </a>
      </div>

      {/* Aviso si el modo de stock está inactivo: detallamos QUÉ deja de estar
          disponible. Ojo: solo afecta a la API de Stock; la de Datos (BI) sigue
          funcionando con el mismo token. */}
      {stockInactivo && (
        <div className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold mb-1">
              Gestión de stock <strong>inactiva</strong>: parte de la API no devolverá datos.
            </p>
            <p className="mb-1">
              <strong>No disponible</strong> (responde <code className="bg-white/70 px-1 rounded">403</code>) —
              los 7 endpoints de la API de Stock: inventario, lotes, historial de movimientos,
              catálogo de productos, resolver código de barras, registrar entradas y registrar salidas.
            </p>
            <p className="mb-1">
              <strong>Sí funciona</strong> con el mismo token — la API de Datos (BI):
              ventas por línea y estados de pedido.
            </p>
            <p className="text-amber-700">
              Para habilitar los de stock, activa un modo: <strong>albarán</strong>, <strong>picking</strong> o <strong>mixto</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Caso A: TOKEN RECIÉN RECLAMADO — visible UNA SOLA VEZ */}
      {tokenJustClaimed && (
        <div className="mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle size={16} className="mt-0.5 text-amber-700" />
            <div>
              <p className="text-xs font-bold text-amber-900">Guarda este token AHORA</p>
              <p className="text-[11px] text-amber-800 mt-0.5">
                No se podrá mostrar de nuevo. Si lo pierdes, tendrás que pedir uno nuevo a CentralSalud.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded border border-amber-200 p-2">
            <code className="flex-1 text-[11px] text-gray-800 font-mono break-all select-all">
              {tokenJustClaimed}
            </code>
            <button
              onClick={handleCopiar}
              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white rounded transition-colors"
              style={{ backgroundColor: copied ? "#10b981" : theme.primary }}
              type="button"
            >
              {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
            </button>
          </div>
          <button
            onClick={() => setTokenJustClaimed(null)}
            className="mt-2 text-[11px] text-amber-700 hover:underline"
            type="button"
          >
            Ya lo he guardado, ocultar
          </button>
        </div>
      )}

      {/* Caso B: hay un token pendiente de reclamar (cliente matriz lo ve) */}
      {!tokenJustClaimed && puedeReclamar && (
        <div className="mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-2 mb-2">
            <Inbox size={18} className="mt-0.5 text-amber-700" />
            <div className="flex-1">
              <p className="text-xs font-bold text-amber-900">Hay un token nuevo esperando</p>
              <p className="text-[11px] text-amber-800 mt-0.5">
                Reclámalo ahora para verlo. Caduca el{" "}
                <strong>{info.pending_expires_at && new Date(info.pending_expires_at).toLocaleString("es-ES")}</strong>.
              </p>
            </div>
            <button
              onClick={reclamarToken}
              disabled={working}
              className="px-3 py-1.5 text-xs font-semibold text-white rounded flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: theme.primary }}
              type="button"
            >
              {working ? <Loader size={12} className="animate-spin" /> : <Key size={12} />}
              Reclamar token
            </button>
          </div>
        </div>
      )}

      {/* Estado actual del token (cuando NO hay claim pendiente ni recién reclamado) */}
      {!tokenJustClaimed && !puedeReclamar && (
        <div className="bg-white rounded-lg border p-3 text-xs space-y-1.5">
          {info?.activo ? (
            <>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                <span className="font-semibold text-emerald-700">Token activo</span>
                {esAdmin && info?.pending_claim && (
                  <span className="text-[10px] text-amber-700 ml-2">
                    Pendiente de reclamación por el cliente
                  </span>
                )}
              </div>
              <div className="text-gray-600 ml-4">
                Creado el <strong>{new Date(info.created_at).toLocaleString("es-ES")}</strong>
                {info.created_by_nombre && <> por <strong>{info.created_by_nombre}</strong></>}
              </div>
              {info.ultima_llamada && (
                <div className="text-gray-600 ml-4">
                  Última llamada: <strong>{new Date(info.ultima_llamada).toLocaleString("es-ES")}</strong>{" "}
                  · Total {info.total_llamadas} llamadas
                </div>
              )}
              {!info.ultima_llamada && (
                <div className="text-gray-400 ml-4 italic">Aún no se ha usado.</div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />
              <span className="text-gray-500">No hay token emitido todavía</span>
            </div>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={handleSolicitar}
          disabled={working}
          className="px-3 py-2 text-xs font-semibold text-white rounded flex items-center gap-1.5 disabled:opacity-50"
          style={{ backgroundColor: theme.primary }}
          type="button"
        >
          {working ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {info?.activo
            ? (esAdmin ? "Regenerar (cliente lo verá)" : "Regenerar token")
            : (esAdmin ? "Solicitar generación" : "Generar token")}
        </button>
        {info?.activo && (
          <button
            onClick={handleRevocar}
            disabled={working}
            className="px-3 py-2 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded flex items-center gap-1.5 disabled:opacity-50"
            type="button"
          >
            <Trash2 size={12} /> Revocar
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
